import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PortailAuthProvider, usePortailAuth } from "./lib/auth-context";
import { PortailRouter, usePortailRouter } from "./lib/router-context";
import { CartProvider } from "./lib/cart-context";
import { PortailLayout } from "./components/PortailLayout";
import { FloatingCart } from "./components/FloatingCart";
import LoginPage from "./pages/LoginPage";
import Dashboard from "./pages/Dashboard";
import SocietePage from "./pages/SocietePage";
import ParametresPage from "./pages/ParametresPage";
import DocumentsPage from "./pages/DocumentsPage";
import CataloguePage from "./pages/CataloguePage";
import PiecesPage from "./pages/PiecesPage";
import TicketsPage from "./pages/TicketsPage";
import TicketDetailPage from "./pages/TicketDetailPage";
import TicketPublicPage from "./pages/TicketPublicPage";
import DevisListPage from "./pages/DevisListPage";
import DevisBuilderPage from "./pages/DevisBuilderPage";
import DevisDetailPage from "./pages/DevisDetailPage";
import RessourcesPage from "./pages/RessourcesPage";
import CommandesPage from "./pages/CommandesPage";
import RobotsActifsPage from "./pages/RobotsActifsPage";
import ActualitesPage from "./pages/ActualitesPage";
import ChatsPage from "./pages/ChatsPage";
import PrestationsPage from "./pages/PrestationsPage";
import NouvelleDemandeWizardPage from "./pages/NouvelleDemandeWizardPage";
import PrestationDetailPage from "./pages/PrestationDetailPage";
import ChatDetailPage from "./pages/ChatDetailPage";
import InstallPage from "./pages/InstallPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import NotFound from "./pages/NotFound";
import { Loader2 } from "lucide-react";
import { PushNotificationsManager } from "./components/PushNotificationsManager";
import { useAppBadge } from "./hooks/useAppBadge";

const portailQueryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000 },
  },
});

function renderPage(path: string) {
  if (path === "/" || path === "") return <Dashboard />;
  if (path === "/societe") return <SocietePage />;
  if (path === "/parametres") return <ParametresPage />;
  if (path === "/documents") return <DocumentsPage />;
  if (path === "/ressources") return <RessourcesPage />;
  if (path.startsWith("/catalogue")) return <CataloguePage />;
  if (path.startsWith("/pieces")) return <PiecesPage />;
  if (path === "/chats") return <ChatsPage />;
  if (path.startsWith("/chats/")) {
    const id = path.split("/chats/")[1];
    return <ChatDetailPage id={id} />;
  }
  if (path === "/tickets") return <TicketsPage />;
  if (path.startsWith("/tickets/")) {
    const id = path.split("/tickets/")[1];
    return <TicketDetailPage id={id} />;
  }
  if (path === "/prestations") return <PrestationsPage />;
  if (path === "/prestations/nouvelle") return <NouvelleDemandeWizardPage />;
  if (path.startsWith("/prestations/")) {
    const id = path.split("/prestations/")[1];
    return <PrestationDetailPage id={id} />;
  }
  if (path === "/commandes") return <CommandesPage />;
  if (path === "/robots") return <RobotsActifsPage />;
  if (path === "/actualites") return <ActualitesPage />;
  if (path === "/install") return <InstallPage />;
  if (path === "/devis") return <DevisListPage />;
  if (path === "/devis/nouveau") return <DevisBuilderPage />;
  if (path.startsWith("/devis/") && path.endsWith("/editer")) {
    const id = path.split("/devis/")[1].replace("/editer", "");
    return <DevisBuilderPage id={id} />;
  }
  if (path.startsWith("/devis/")) {
    const id = path.split("/devis/")[1];
    return <DevisDetailPage id={id} />;
  }
  return <NotFound />;
}

const AUTH_ONLY_PATHS = ["/login", "/connexion", "/forgot-password"];

function AppRouter() {
  const { isAuthenticated, isLoading } = usePortailAuth();
  const { path, navigate } = usePortailRouter();
  useAppBadge('/api/portail/chats/unread-count', isAuthenticated && !isLoading);

  useEffect(() => {
    if (isAuthenticated && AUTH_ONLY_PATHS.includes(path)) {
      navigate("/");
    }
  }, [isAuthenticated, path, navigate]);

  if (path === "/auth/callback") return <AuthCallbackPage />;
  if (path === "/reset-password") return <ResetPasswordPage />;

  if (path === "/ticket-public") {
    return <TicketPublicPage />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <CartProvider>
      <PortailLayout>
        {renderPage(path)}
      </PortailLayout>
      <FloatingCart />
      <PushNotificationsManager />
    </CartProvider>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={portailQueryClient}>
      <PortailRouter>
        <PortailAuthProvider>
          <AppRouter />
        </PortailAuthProvider>
      </PortailRouter>
    </QueryClientProvider>
  );
}
